import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { SensorMasterService } from '../../../core/services/sensor-master.service';
import { OrganizationManageService } from '../../../core/services/organization-manage.service';
import { DialogService } from '../../../core/services/dialog.service';
import { SensorFormComponent } from '../sensor-form/sensor-form.component';
import { firstValueFrom } from 'rxjs';
import { Sensor } from '../../../shared/interfaces/sensor.interface';

export interface SensorFilterCriteria {
  id: string;
  organizationId: string | null;
  categoryName: string | null;
  siteStatus: string | null;
}

export interface SensorListItem extends Sensor {
  organizationName: string;
}

@Component({
  selector: 'app-sensor-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './sensor-list.component.scss',
  imports: [
    FormsModule,
    MatTableModule,
    MatSortModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatMenuModule,
    MatSnackBarModule,
    MatDialogModule
  ],
  template: `
    <div class="p-6">
      <div class="flex justify-between items-center mb-6">
        <h1 class="text-2xl font-bold">センサー（マスター）管理</h1>
        <button matButton="tonal" color="primary" (click)="openForm()">
          <mat-icon>add</mat-icon>
          センサー追加
        </button>
      </div>

      @if (sensorService.isLoading() || orgService.isLoading()) {
        <div class="flex justify-center items-center py-12">
          <mat-spinner diameter="40"></mat-spinner>
        </div>
      } @else if (sensorService.error()) {
        <div class="bg-red-50 text-red-600 p-4 rounded-lg">
          {{ sensorService.error() }}
        </div>
      } @else {
        <div class="bg-primary-container p-4 rounded-lg shadow-sm mb-6">
          <div class="flex flex-wrap gap-4 items-center">
            <mat-form-field appearance="outline" class="w-full sm:w-auto">
              <mat-label>センサーIDで検索</mat-label>
              <input matInput [ngModel]="filterCriteria().id" (ngModelChange)="updateFilter('id', $event)" placeholder="センサーIDを入力">
              <mat-icon matSuffix>search</mat-icon>
            </mat-form-field>

            <mat-form-field appearance="outline" class="w-full sm:w-auto">
              <mat-label>組織で絞り込み</mat-label>
              <mat-select [ngModel]="filterCriteria().organizationId" (ngModelChange)="updateFilter('organizationId', $event)">
                <mat-option [value]="null">すべて</mat-option>
                @for (org of orgService.organizations(); track org.id) {
                  <mat-option [value]="org.id">{{ org.name }}</mat-option>
                }
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" class="w-full sm:w-auto">
              <mat-label>カテゴリーで絞り込み</mat-label>
              <mat-select [ngModel]="filterCriteria().categoryName" (ngModelChange)="updateFilter('categoryName', $event)">
                <mat-option [value]="null">すべて</mat-option>
                <mat-option value="WBGT">WBGT</mat-option>
                <mat-option value="WIND">WIND</mat-option>
                <mat-option value="NOISE">NOISE</mat-option>
                <mat-option value="PH">PH</mat-option>
                <mat-option value="TILT">TILT</mat-option>
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" class="w-full sm:w-auto">
              <mat-label>現場割当状態</mat-label>
              <mat-select [ngModel]="filterCriteria().siteStatus" (ngModelChange)="updateFilter('siteStatus', $event)">
                <mat-option [value]="null">すべて</mat-option>
                <mat-option value="assigned">割当済</mat-option>
                <mat-option value="unassigned">未割当</mat-option>
              </mat-select>
            </mat-form-field>

            <div class="grow flex justify-end">
              <button matButton color="warn" (click)="clearFilters()">
                <mat-icon>clear_all</mat-icon>
                条件をクリア
              </button>
            </div>
          </div>
        </div>

        @if (dataSource().length === 0) {
          <div class="bg-primary-container p-12 text-center rounded-lg shadow-sm">
            <mat-icon class="text-4xl text-gray-400 mb-2">sensors</mat-icon>
            <p class="text-gray-500">条件に一致するセンサーがありません</p>
          </div>
        } @else {
          <div class="bg-primary-container rounded-lg shadow-sm overflow-hidden">
            <table mat-table [dataSource]="dataSource()" matSort matSortActive="id" matSortDirection="asc" (matSortChange)="onSortChange($event)" class="w-full">
              
              <ng-container matColumnDef="id">
                <th mat-header-cell *matHeaderCellDef mat-sort-header> センサーID </th>
                <td mat-cell *matCellDef="let sensor"> {{sensor.id}} </td>
              </ng-container>

              <ng-container matColumnDef="organization">
                <th mat-header-cell *matHeaderCellDef mat-sort-header> 所属組織 </th>
                <td mat-cell *matCellDef="let sensor"> {{ sensor.organizationName }} </td>
              </ng-container>

              <ng-container matColumnDef="category">
                <th mat-header-cell *matHeaderCellDef mat-sort-header> カテゴリー </th>
                <td mat-cell *matCellDef="let sensor"> 
                  <span class="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                    {{sensor.categoryName}}
                  </span>
                </td>
              </ng-container>

              <ng-container matColumnDef="site">
                <th mat-header-cell *matHeaderCellDef mat-sort-header> 現場割当 </th>
                <td mat-cell *matCellDef="let sensor">
                  @if (sensor.siteId) {
                    <span class="text-green-600 text-sm">割当済 ({{ sensor.siteId }})</span>
                  } @else {
                    <span class="text-gray-400 text-sm">未割当</span>
                  }
                </td>
              </ng-container>

              <ng-container matColumnDef="actions">
                <th mat-header-cell *matHeaderCellDef class="w-16"></th>
                <td mat-cell *matCellDef="let sensor">
                  <button matIconButton aria-label="センサー操作" [matMenuTriggerFor]="menu">
                    <mat-icon>more_vert</mat-icon>
                  </button>
                  <mat-menu #menu="matMenu" xPosition="before">
                    <button mat-menu-item (click)="openForm(sensor)">
                      <mat-icon>edit</mat-icon>
                      <span>編集</span>
                    </button>
                    <button mat-menu-item (click)="deleteSensor(sensor.id)">
                      <mat-icon class="text-red-600!">delete</mat-icon>
                      <span class="text-red-600!">削除</span>
                    </button>
                  </mat-menu>
                </td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
              <tr mat-row *matRowDef="let row; columns: displayedColumns;" class="hover:bg-gray-50"></tr>
            </table>
          </div>
        }
      }
    </div>
  `
})
export class SensorListComponent {
  protected readonly sensorService = inject(SensorMasterService);
  protected readonly orgService = inject(OrganizationManageService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialogService = inject(DialogService);
  private readonly dialog = inject(MatDialog);
  
  public displayedColumns: string[] = ['id', 'organization', 'category', 'site', 'actions'];

  public filterCriteria = signal<SensorFilterCriteria>({
    id: '',
    organizationId: null,
    categoryName: null,
    siteStatus: null,
  });

  public sortState = signal<Sort>({ active: 'id', direction: 'asc' });

  public dataSource = computed<SensorListItem[]>(() => {
    const sensors = this.sensorService.sensors();
    const orgs = this.orgService.organizations();
    const filter = this.filterCriteria();
    const sort = this.sortState();

    const orgMap = new Map(orgs.map(o => [o.id, o.name]));

    let filtered = sensors.filter(sensor => {
      let matchId = true;
      if (filter.id) {
        const searchTerms = filter.id.toLowerCase().split(/[\s　]+/).filter(t => t.length > 0);
        const targetId = sensor.id.toLowerCase();
        matchId = searchTerms.every(term => targetId.includes(term));
      }
      const matchOrg = !filter.organizationId || sensor.organizationId === filter.organizationId;
      const matchCat = !filter.categoryName || sensor.categoryName === filter.categoryName;
      let matchSite = true;
      if (filter.siteStatus === 'assigned') {
        matchSite = !!sensor.siteId;
      } else if (filter.siteStatus === 'unassigned') {
        matchSite = !sensor.siteId;
      }
      return matchId && matchOrg && matchCat && matchSite;
    });

    let processed: SensorListItem[] = filtered.map(sensor => ({
      ...sensor,
      organizationName: orgMap.get(sensor.organizationId) || sensor.organizationId
    }));

    if (sort.active && sort.direction !== '') {
      processed.sort((a, b) => {
        const isAsc = sort.direction === 'asc';
        let valA: string | number = '';
        let valB: string | number = '';

        switch (sort.active) {
          case 'id':
            valA = a.id; valB = b.id; break;
          case 'organization':
            valA = a.organizationName; valB = b.organizationName; break;
          case 'category':
            valA = a.categoryName; valB = b.categoryName; break;
          case 'site':
            valA = a.siteId || ''; valB = b.siteId || ''; break;
        }

        return String(valA).localeCompare(String(valB), 'ja') * (isAsc ? 1 : -1);
      });
    }

    return processed;
  });

  public onSortChange(sort: Sort): void {
    this.sortState.set(sort);
  }

  public updateFilter<K extends keyof SensorFilterCriteria>(key: K, value: SensorFilterCriteria[K]): void {
    this.filterCriteria.update(criteria => ({
      ...criteria,
      [key]: value
    }));
  }

  public clearFilters(): void {
    this.filterCriteria.set({
      id: '',
      organizationId: null,
      categoryName: null,
      siteStatus: null,
    });
  }

  public async openForm(sensor?: Sensor): Promise<void> {
    const dialogRef = this.dialog.open(SensorFormComponent, {
      width: '500px',
      data: { sensor }
    });

    const result = await firstValueFrom(dialogRef.afterClosed());
    if (result) {
      try {
        if (sensor) {
          const { id, ...updateData } = result;
          await this.sensorService.updateSensor(sensor.id, updateData);
          this.snackBar.open('センサー情報を更新しました', '閉じる', { duration: 3000 });
        } else {
          await this.sensorService.createSensor(result);
          this.snackBar.open('新規センサーを登録しました', '閉じる', { duration: 3000 });
        }
        this.sensorService.reloadSensors(); 
      } catch (error) {
        console.error('Failed to save sensor', error);
        this.snackBar.open('センサーの保存に失敗しました', '閉じる', { duration: 3000 });
      }
    }
  }

  public async deleteSensor(id: string): Promise<void> {
    const confirmed = await firstValueFrom(this.dialogService.confirm({
      title: '確認',
      message: `センサー「${id}」を削除してもよろしいですか？`,
      confirmText: '削除',
      cancelText: 'キャンセル',
      color: 'warn'
    }));

    if (confirmed) {
      try {
        await this.sensorService.deleteSensor(id);
        this.snackBar.open('センサーを削除しました', '閉じる', { duration: 3000 });
        this.sensorService.reloadSensors();
      } catch (error: unknown) {
        console.error('Failed to delete sensor ->', error);
        const message = error instanceof Error ? error.message : '不明なエラー';
        this.snackBar.open(`センサーの削除に失敗しました: ${message}`, '閉じる', { duration: 5000 });
      }
    }
  }
}
